import type {ProCoverageItem} from '../../../types';

import {Loading} from '@ds/components';
import {cn} from '@ds/helpers';
import {useEffect, useMemo, useState} from 'react';
import {useAppDispatch, useAppSelector} from 'store';
import {getVendorProPlans} from 'store/slices/vendor-pro/thunk';

import {buildCoverageMapPolygons, getCoveragePolygonIds} from '../../../utils';
import ProCoverageMap from '../ProCoverageMap/ProCoverageMap';
import ProEditableZone from './components/ProEditableZone/ProEditableZone';
import ProReadOnlyZone from './components/ProReadOnlyZone/ProReadOnlyZone';

type ProAreaSelectProps = {
  vendorId?: string | number;
  onChange?: (data: {
    polygonIds?: Array<string | number>;
    selectedPolygons?: Record<string, unknown>;
  }) => void;
  readOnly?: boolean;
  coverageItems?: ProCoverageItem[];
  showMap?: boolean;
  showHeader?: boolean;
};

const ProAreaSelect: React.FC<ProAreaSelectProps> = ({
  vendorId,
  onChange,
  readOnly = false,
  coverageItems,
  showMap = true,
  showHeader = true
}) => {
  const dispatch = useAppDispatch();
  const {plans, plansLoading} = useAppSelector((state) => state.vendorPro);
  const [selectedCountByType, setSelectedCountByType] = useState<Record<string, number>>({});
  const items = (readOnly ? coverageItems : plans?.items) as ProCoverageItem[] | undefined;

  const selectedPolygonIds = useMemo(() => {
    if (!items?.length) {
      return [];
    }

    if (readOnly) {
      return getCoveragePolygonIds(items);
    }

    const ids: Array<string | number> = [];
    items.forEach((item) => {
      const count = selectedCountByType[item.type] || 0;
      item.polygons.slice(0, count).forEach((polygon) => {
        ids.push(polygon.polygon_id);
      });
    });
    return ids;
  }, [items, selectedCountByType, readOnly]);

  useEffect(() => {
    if (!readOnly && vendorId) {
      void dispatch(getVendorProPlans({vendor_id: Number(vendorId)}));
    }
  }, [dispatch, vendorId, readOnly]);

  const latitude = plans?.latitude ?? 35.6892;
  const longitude = plans?.longitude ?? 51.389;
  const polygons = useMemo(
    () => buildCoverageMapPolygons(items, selectedPolygonIds, !readOnly),
    [items, selectedPolygonIds, readOnly]
  );

  useEffect(() => {
    if (readOnly || !onChange) {
      return;
    }

    const selectedPolygons = Object.fromEntries(
      Object.entries(selectedCountByType).filter(([, count]) => count > 0)
    );
    onChange({polygonIds: selectedPolygonIds, selectedPolygons});
    // The parent only needs the latest selected counts; re-running on onChange identity is unnecessary.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPolygonIds, selectedCountByType, items, readOnly]);

  const setZoneCount = (type: string, count: number, max: number) => {
    const nextCount = Math.max(0, Math.min(count, max));
    setSelectedCountByType((current) => ({...current, [type]: nextCount}));
  };

  if (!readOnly && plansLoading) {
    return (
      <div className="flex min-h-50 flex-1 items-center justify-center">
        <Loading size="large" />
      </div>
    );
  }

  if (!items?.length) {
    return null;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      <div className={cn(readOnly && !showMap ? '' : 'px-4 pb-4')}>
        {showHeader ? (
          <>
            <h3 className="mb-1 text-subtitle-md text-neutral-fg">منطقه تحت پوشش</h3>
            <p className="m-0 text-body-sm text-gray-34">
              پوشش از محدوده نزدیک تا محدوده دور به‌صورت پیوسته انتخاب می‌شود.
            </p>
          </>
        ) : null}
        {showMap ? (
          <ProCoverageMap
            latitude={latitude}
            longitude={longitude}
            polygons={polygons}
            selectedPolygonIds={selectedPolygonIds}
          />
        ) : null}
        {items.map((item) =>
          readOnly ? (
            <ProReadOnlyZone key={item.type} item={item} />
          ) : (
            <ProEditableZone
              key={item.type}
              item={item}
              selectedCount={selectedCountByType[item.type] || 0}
              onCountChange={(count) => setZoneCount(item.type, count, item.polygons.length)}
            />
          )
        )}
      </div>
    </div>
  );
};

export default ProAreaSelect;
